import { Link, NavLink } from '@remix-run/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { LuShoppingCart } from 'react-icons/lu'
import { useCart } from '#app/hooks/useCart.tsx'
import { LINKS, SECONDARY_LINKS } from '../config/constants.tsx'
import { H1, H3 } from './typography'

const linksWithoutHome = LINKS.slice(1)
const secondaryLinks = SECONDARY_LINKS
const MOBILE_LINKS = [
	{ name: 'Home', to: '/' },
	...linksWithoutHome,
	...secondaryLinks,
]

const MobileMenuList = ({ isExpanded, handleMenuClick }: any) => {
	useEffect(() => {
		if (isExpanded) {
			document.body.classList.add('fixed')
			document.body.classList.add('overflow-y-scroll')
			document.body.classList.add('w-full')
			document.body.style.height = '100vh'
		} else {
			document.body.classList.remove('fixed')
			document.body.classList.remove('overflow-y-scroll')
			document.body.classList.remove('w-full')
			document.body.style.removeProperty('height')
		}
	}, [isExpanded])

	return (
		<AnimatePresence>
			{isExpanded ? (
				<div className="absolute bottom-0 left-0 right-0 top-[70px] z-50 block">
					<motion.div
						initial={{ y: -50, opacity: 0 }}
						animate={{ y: 0, opacity: 1 }}
						exit={{ y: -50, opacity: 0 }}
						transition={{
							duration: 0.25,
							ease: 'linear',
						}}
						className="flex h-full flex-col overflow-y-scroll bg-white pb-8"
					>
						<div className="flex flex-col border-none bg-transparent p-0">
							{MOBILE_LINKS.map((link, i) => (
								<NavLink
									onClick={handleMenuClick}
									key={`${i}+${link.name}`}
									to={link.to}
									className="border-b border-gray-200 px-3 py-3 uppercase text-primary"
								>
									<H3 className="mt-4">{link.name}</H3>
								</NavLink>
							))}
						</div>
					</motion.div>
				</div>
			) : null}
		</AnimatePresence>
	)
}

const topVariants = {
	open: { rotate: 45, y: 7, originX: '16px', originY: '10px' },
	closed: { rotate: 0, y: 0, originX: 0, originY: 0 },
}

const centerVariants = {
	open: { opacity: 0 },
	closed: { opacity: 1 },
}

const bottomVariants = {
	open: { rotate: -45, y: -5, originX: '16px', originY: '22px' },
	closed: { rotate: 0, y: 0, originX: 0, originY: 0 },
}

const MobileMenu = ({ children }: { children: React.ReactNode }) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const handleMenuClick = () => setIsExpanded(!isExpanded)
	const state = isExpanded ? 'open' : 'closed'
	const transition = {}
	return (
		<>
			<button onClick={handleMenuClick}>
				<svg
					width="32"
					height="32"
					viewBox="0 0 32 32"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<motion.rect
						animate={state}
						variants={topVariants}
						transition={transition}
						x="6"
						y="9"
						width="20"
						height="2"
						rx="1"
						fill="currentColor"
					/>
					<motion.rect
						animate={state}
						variants={centerVariants}
						transition={transition}
						x="6"
						y="15"
						width="20"
						height="2"
						rx="1"
						fill="currentColor"
					/>
					<motion.rect
						animate={state}
						variants={bottomVariants}
						transition={transition}
						x="6"
						y="21"
						width="20"
						height="2"
						rx="1"
						fill="currentColor"
					/>
				</svg>
			</button>
			{children}
			<MobileMenuList
				isExpanded={isExpanded}
				handleMenuClick={handleMenuClick}
			/>
		</>
	)
}

export default function Header({ titles }: any) {
	const { cartId } = useCart()

	return (
		<header className="row-start-1 row-end-2 lg:row-start-1 lg:row-end-2">
			<div className="mb-2 bg-secondary">
				<div className="mx-auto hidden max-w-7xl justify-end gap-10 py-2 lg:flex xl:w-11/12 2xl:w-10/12">
					{SECONDARY_LINKS.map((item, i) => (
						<Link className="uppercase" key={`${i}+${item.name}`} to={item.to}>
							{item.name}
						</Link>
					))}
					{/* Grab cartId from sessionStorage and append to URL */}
					<Link
						to={
							cartId
								? `/winkelwagen?cartId=${encodeURIComponent(cartId)}`
								: '/winkelwagen'
						}
					>
						<LuShoppingCart size={'24px'} />
					</Link>
				</div>
			</div>
			<div className="flex items-center justify-between border-b border-b-gray-200 px-8 py-4 lg:hidden">
				<MobileMenu>
					<Link
						to={
							cartId
								? `/winkelwagen?cartId=${encodeURIComponent(cartId)}`
								: '/winkelwagen'
						}
					>
						<LuShoppingCart size={'24px'} />
					</Link>
				</MobileMenu>
			</div>
			<div className="mt-1 flex flex-col items-center lg:mb-8">
				<div>
					<Link to="/">
						{/* <H1>{titles.attributes.title}</H1> */}
						<H1>Wine with Margaret</H1>
					</Link>
					<div className="flex items-center justify-center">
						<div className="block flex-grow border-t border-gray-300 lg:border-t-2"></div>
						{/* <H3 className="mx-8 flex-shrink">{titles.attributes.subtitle}</H3> */}
						<H3 className="mx-8 flex-shrink">Never Miss a Sip</H3>
						<div className="block flex-grow border-t border-gray-300 lg:block"></div>
					</div>
				</div>
			</div>
		</header>
	)
}
